/**
 * Shared types for the AI-assisted structured note field.
 *
 * Notes are stored either as plain text (no AI structuring used) or as a JSON
 * payload that keeps both the original free-text and the structured sections.
 */

export type {
  StructuredNoteTextItem,
  StructuredNoteNextStep,
  StructuredNoteSections,
  StructuredNotePayload
} from '@/utils/structuredNoteSchema';
import type { StructuredNoteSections, StructuredNotePayload } from '@/utils/structuredNoteSchema';

/**
 * UI modes for {@link StructuredNoteField}:
 * - `compose-plain` — single original textarea before any AI run
 * - `compose-split` — original + structured editors side by side
 * - `display` — read-only structured view after reopening a saved note
 */
export type StructuredNoteViewMode = 'compose-plain' | 'compose-split' | 'display';

/** Result of parsing a stored note that is still plain text. */
export interface ParsedPlainNote {
  mode: 'plain';
  content: string;
}

/** Result of parsing a stored note that contains a structured payload. */
export interface ParsedStructuredNote {
  mode: 'structured';
  payload: StructuredNotePayload;
}

/** Discriminated union returned by {@link parseNoteValue}. */
export type ParsedNoteValue = ParsedPlainNote | ParsedStructuredNote;

/** Internal UI state held by {@link StructuredNoteField}. */
export interface StructuredNoteFieldState {
  viewMode: StructuredNoteViewMode;
  originalContent: string;
  structuredNote: StructuredNoteSections;
  /**
   * Snapshot of the last AI-generated structured note (JSON string).
   * Used to detect manual edits before regenerating.
   */
  lastAiSnapshot: string | null;
}

/** Empty structured sections used before the first AI generation. */
export const EMPTY_STRUCTURED_NOTE: StructuredNoteSections = {
  keyTakeaways: [],
  agreementsDecisions: [],
  nextSteps: [],
};
